'use client';

import {
  Accordion,
  AccordionDetails,
  AccordionItem,
  AccordionSummary,
} from '@packages/shared/src/ui/Accordion/Accordion';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { FocusScope } from '@react-aria/focus';
import React, { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { classes } from '../../../utils/classes';
import { subscribeSheetMessages } from './NewsletterSheet.messages';

export type SheetProps = {
  /** Title that opens the sheet */
  title: string;
  /** Content in the opened sheet */
  children: React.ReactNode;
  /** Function that is called when the user clicks the X Button */
  onClickClose: () => void;
};

/**
 * A small sheet on the bottom screen that opens when you click on it
 */
export const Sheet = ({ title, children, onClickClose }: SheetProps) => {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();

  const forceHideSheet = (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    onClickClose();
    e.stopPropagation();
  };

  const uniqueId = useId();

  return (
    <div className="nl-bottom-sheet z-modal pointer-events-none fixed bottom-0 left-0 w-full overflow-hidden">
      {/* Curtain */}
      <div
        aria-hidden="true"
        className={classes(
          'bg-color-backdrop fixed top-0 left-0 size-full transition-opacity',
          open
            ? 'pointer-events-auto opacity-100 duration-[1200ms] ease-in'
            : 'pointer-events-none opacity-0 duration-[1500ms] ease-out',
        )}
        onClick={() => {
          setOpen(false);
        }}
      />
      <FocusScope contain={open} restoreFocus>
        <Accordion
          collapsible
          value={open ? 'open' : 'closed'}
          type="single"
          className="bg-color-transparent"
          onValueChange={(isExpanded) => setOpen(!!isExpanded)}
          {...(open && {
            // As this accordion behaves like a modal, additional attributes are needed:
            // https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
            role: 'dialog',
            'aria-modal': 'true',
            'aria-labelledby': `${uniqueId}-heading`,
            onKeyDown: (e) => {
              if (e.key === 'Escape') {
                // close dialog if ESC is pressed
                setOpen(false);
              }
            },
          })}
        >
          <AccordionItem value="open" className="_c_cms_nlSheet_accordion_item">
            <AccordionSummary
              className="_c_cms_nlSheet_accordion_summary"
              icon="icon-emp-arrow-large-up"
            >
              <span className="icon-emp-mail bg-color-text-dark md:bg-color-primary-contrasttext size-4" />
              <div className="w-full text-left" id={`${uniqueId}-heading`}>
                {title}
              </div>
            </AccordionSummary>

            {/* Content */}
            <AccordionDetails className="_c_cms_nlSheet_accordion_details">
              {children}
              <Button
                variant="icon-only"
                className="bg-color-transparent absolute top-0 right-0 hidden border-none bg-none md:block"
                onClick={forceHideSheet}
                aria-label={formatMessage(subscribeSheetMessages.closePermanent)}
              >
                <span className="icon-emp-cancel" />
              </Button>
            </AccordionDetails>
          </AccordionItem>
        </Accordion>
      </FocusScope>
    </div>
  );
};
